/**
 * Filename Input Dialog Component
 * Dialog for entering a custom filename when uploading documents
 */

import { Component, ChangeDetectionStrategy, inject, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialogModule } from '@angular/material/dialog';
import { TranslatePipe, I18nStore } from '../../core/features/translations';

/**
 * Dialog data interface
 */
export interface FilenameInputDialogData {
  originalFilename: string;
  suggestedFilename: string;
}

/**
 * Dialog result interface
 */
export interface FilenameInputDialogResult {
  filename: string;
  cancelled: boolean;
}

@Component({
  selector: 'app-filename-input-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatDialogModule,
    TranslatePipe,
  ],
  template: `
    <div class="p-6 font-sans">
      <h2 class="m-0 mb-4 text-xl font-bold text-gray-900 border-b pb-3">
        {{ 'portal.documents.dialog.title' | translate }}
      </h2>
      
      <div class="py-4">
        <p class="mt-0 mb-4 text-sm text-gray-500 leading-relaxed font-medium italic">
          {{ 'portal.documents.dialog.description' | translate }}
        </p>
        
        <div class="mb-6 p-4 bg-gray-50 rounded border border-gray-200">
          <p class="m-0 text-sm text-gray-700">
            {{ 'portal.documents.dialog.original' | translate }}:
            <strong class="text-gray-900 break-all ml-1">{{ data.originalFilename }}</strong>
          </p>
        </div>

        <div class="flex flex-col gap-1.5 w-full">
          <label class="text-xs font-bold text-gray-400 uppercase tracking-widest ml-1">
            {{ 'portal.documents.dialog.filename' | translate }}
          </label>
          <div class="relative">
            <input
              type="text"
              [(ngModel)]="filename"
              [maxlength]="maxLength"
              [placeholder]="'portal.documents.dialog.filenamePlaceholder' | translate"
              (keyup.enter)="onConfirm()"
              autofocus
              class="w-full p-3 bg-white border-2 border-gray-100 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm transition-all duration-200 placeholder:text-gray-300"
              [class.border-red-400]="errorMessage()"
            />
            <div class="absolute right-3 bottom-0.5 text-[10px] font-bold text-gray-300 uppercase">
              {{ characterCount() }} / {{ maxLength }}
            </div>
          </div>
          
          @if (errorMessage()) {
            <span class="text-xs text-red-500 font-bold ml-1 animate-slide-down flex items-center gap-1 mt-1">
              <span class="material-symbols-outlined text-sm">error</span>
              {{ errorMessage() }}
            </span>
          }
        </div>
      </div>

      <div class="flex justify-end gap-3 mt-6 pt-5 border-t border-gray-50">
        <button 
          type="button"
          (click)="onCancel()"
          class="px-5 py-2.5 text-sm font-bold text-gray-500 bg-white border-2 border-gray-50 rounded-lg transition-all duration-200 hover:bg-gray-50 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-200"
        >
          {{ 'portal.documents.dialog.cancel' | translate }}
        </button>
        <button 
          type="button"
          (click)="onConfirm()" 
          [disabled]="!isValid()"
          class="flex items-center gap-2 px-6 py-2.5 text-sm font-bold text-white bg-blue-600 rounded-lg shadow-md transition-all duration-200 hover:bg-blue-700 hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-30 disabled:cursor-not-allowed disabled:shadow-none"
        >
          <span class="material-symbols-outlined text-base">upload</span>
          {{ 'portal.documents.dialog.upload' | translate }}
        </button>
      </div>
    </div>
  `,
  styles: [],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FilenameInputDialogComponent {
  readonly dialogRef = inject(MatDialogRef<FilenameInputDialogComponent>);
  readonly data = inject<FilenameInputDialogData>(MAT_DIALOG_DATA);
  private readonly i18n = inject(I18nStore);

  readonly maxLength = 180;
  filename = this.data.suggestedFilename;

  // Computed signals
  readonly characterCount = computed(() => this.filename?.length || 0);
  readonly errorMessage = signal<string | null>(null);
  readonly isValid = computed(() => {
    const name = this.filename?.trim();
    return name && name.length > 0 && name.length <= this.maxLength;
  });

  /**
   * Handle confirm button click
   */
  onConfirm(): void {
    const trimmedFilename = this.filename?.trim();

    if (!trimmedFilename) {
      this.errorMessage.set(this.i18n.t('portal.documents.dialog.error.empty'));
      return;
    }

    if (trimmedFilename.length > this.maxLength) {
      this.errorMessage.set(
        this.i18n.t('portal.documents.dialog.error.tooLong', {
          maxLength: this.maxLength.toString(),
        })
      );
      return;
    }

    // Check for invalid characters
    const invalidChars = /[<>:"/\\|?*]/g;
    if (invalidChars.test(trimmedFilename)) {
      this.errorMessage.set(this.i18n.t('portal.documents.dialog.error.invalidChars'));
      return;
    }

    const result: FilenameInputDialogResult = {
      filename: trimmedFilename,
      cancelled: false,
    };

    this.dialogRef.close(result);
  }

  /**
   * Handle cancel button click
   */
  onCancel(): void {
    const result: FilenameInputDialogResult = {
      filename: '',
      cancelled: true,
    };

    this.dialogRef.close(result);
  }
}
